import FormInputTextField from "../FormInputTextField/FormInputTextField.vue";
import FormCheckbox from "../FormCheckbox/FormCheckbox.vue";
import FormInputDropdown from "../FormInputDropdown/FormInputDropdown.vue";
import FormAddressField from "../FormAddressField/FormAddressField.vue";
import FormRadioGroup from "../FormRadioGroup/FormRadioGroup.vue";
import { ref, computed, watch, onBeforeMount } from "vue";
import GetLabel from "foundation/js/getLabel";
import fieldsConfig from "./config/fieldsConfig";
import { useStore } from "vuex";
import Editor from '@hugerte/hugerte-vue'
import { useHugeRTE } from '../../foundation/js/loadHugeRTE'

export default {
    name: "CreateEvent",
    props: {
        groupId: {
            type: String,
            required: true
        },
        isEditContext: {
            type: Boolean,
            required: true,
        },
        userInfo: {
            type: Object,
            required: false,
        }
    },
    components: {
        FormInputTextField,
        FormCheckbox,
        FormInputDropdown,
        FormAddressField,
        FormRadioGroup,
        Editor
    },
    setup(props) {
        const store = useStore()
        const displayError = ref(false)      
        const getLabel = (labelKey) => GetLabel({ labelKey, labelCollection: 'CreateEvent' })

        const submitPath = props.isEditContext == true ? 'frivillige/updateEvent' : 'frivillige/createEvent'
        const isEditContext = props.isEditContext

        const nameOfEvent = ref(props.userInfo?.nameOfEvent ?? "") 
        const description = ref(props.userInfo?.description ?? "")
        const shareInMainCalendar = ref(props.userInfo?.shareInMainCalendar ?? false)
        const mandatoryRegistration = ref(props.userInfo?.mandatoryRegistration ?? false)
        const maxParticipants = ref(props.userInfo?.maxParticipants)
        const contactPersonOther = ref(props.userInfo?.contactPersonName?.length > 0 ?? false)
        const contactPersonEmail = ref(props.userInfo?.contactPersonEmail)
        const contactPersonPhone = ref(props.userInfo?.contactPersonPhone)
        const contactPersonName = ref(props.userInfo?.contactPersonName)
        const startDate = ref(props.userInfo?.startDate ?? "")
        const startTime = ref(props.userInfo?.startTime ?? "")
        const endDate = ref(props.userInfo?.endDate ?? "")
        const endTime = ref(props.userInfo?.endTime ?? "")
        const address = ref(props.userInfo?.address ?? "")
        const postalCode = ref(props.userInfo?.postalCode ?? "")
        const city = ref(props.userInfo?.city ?? "")
        const showSpinner = ref(false)
        const { editorReady } = useHugeRTE()
        
        const contactTypeEnum = Object.freeze({
            SELF: "SELF",
            OTHER: "OTHER"
        })
        const contactType = ref(contactTypeEnum.SELF)
        const contactTypeRadioOptions = [
            {
                value: contactTypeEnum.SELF,
                label: getLabel('contactPersonMyselfLabel')
            },
            {
                value: contactTypeEnum.OTHER,
                label: getLabel('contactPersonOtherLabel')
            }
        ]
        const contactPersonMyself = computed(() => contactType.value === contactTypeEnum.SELF)

        onBeforeMount(() => {
            if (props.userInfo) {
                Object.keys(props.userInfo).forEach(function (key) {
                    const field = fieldsConfig.find(field => field.name === key)
                    if (field) {
                        field.value = props.userInfo[key]
                        field.isValid = field.validator(props.userInfo[key])
                    }
                })
            }
        })

        const updateConfigAndReturnValidState = (name, value) => {
            fieldsConfig.find(field => field.name === name).value = value
            const valid = fieldsConfig.find(field => field.name === name).validator(value)
            fieldsConfig.find(field => field.name === name).isValid = valid
            return valid
        }

        const showError = (name, value) => {
            const valid = updateConfigAndReturnValidState(name, value)
            return displayError.value && !valid
        }

        const showErrorAddress = computed(() => {
            const validAddress = updateConfigAndReturnValidState('address', address.value)
            const validPostalCode = updateConfigAndReturnValidState('postalCode', postalCode.value)
            const validCity = updateConfigAndReturnValidState('city', city.value)

            return displayError.value && (!validAddress || !validPostalCode || !validCity)
        })

        const startDateValidator = (startDateStr, startTimeStr) => {

            if (!startDateStr || !startTimeStr) return false
            
            var now = new Date()
            var startTimestamp = new Date(`${startDateStr} ${startTimeStr}`)
            
            return now <= startTimestamp
        } 
        
        const endDateValidator = (startDateStr, startTimeStr, endDateStr, endTimeStr) => {
            
            // Only show error for start date+time if these are not set
            if (!startDateStr || !startTimeStr) return true
            
            if (!endDateStr || !endTimeStr) return false
            
            var startTimestamp = new Date(`${startDateStr} ${startTimeStr}`)
            var endTimestamp = new Date(`${endDateStr} ${endTimeStr}`)
            return startTimestamp <= endTimestamp
        }
 
        const isStartDateValid = computed(() => {
            fieldsConfig.find(field => field.name === 'startDate').isValid = startDateValidator(startDate.value, startTime.value)
            fieldsConfig.find(field => field.name === 'startDate').value = startDate.value
            return displayError.value && !startDateValidator(startDate.value, startTime.value)
        })

        const isEndDateValid = computed(() => {
            fieldsConfig.find(field => field.name === 'endDate').isValid = endDateValidator(startDate.value, startTime.value, endDate.value, endTime.value)
            fieldsConfig.find(field => field.name === 'endDate').value = startDate.value
            return displayError.value && !endDateValidator(startDate.value, startTime.value, endDate.value, endTime.value)
        })
        
        watch(description, (newValue) => {
            showError('description', newValue)
        })

        const validateFields = () => {
            // Update fieldsConfig with some custom validation
            if (!contactPersonOther.value) {
                fieldsConfig.find(field => field.name === 'contactPersonEmail').isValid = true
                fieldsConfig.find(field => field.name === 'contactPersonEmail').value = ""
                fieldsConfig.find(field => field.name === 'contactPersonName').isValid = true
                fieldsConfig.find(field => field.name === 'contactPersonName').value = ""
                fieldsConfig.find(field => field.name === 'contactPersonPhone').isValid = true
                fieldsConfig.find(field => field.name === 'contactPersonPhone').value = ""                                
            }
            
            return fieldsConfig.every(field => field.isValid)
        }

        const getFields = () => {
            return {
                nameOfEvent: nameOfEvent.value,
                description: description.value,
                shareInMainCalendar: shareInMainCalendar.value,
                mandatoryRegistration: mandatoryRegistration.value,
                maxParticipants: maxParticipants.value,
                contactPersonMyself: contactPersonMyself.value,
                contactPersonOther: contactPersonOther.value,
                contactPersonEmail: contactPersonEmail.value,
                contactPersonPhone: contactPersonPhone.value,
                contactPersonName: contactPersonName.value,
                startDate: startDate.value,
                startTime: startTime.value,
                endDate: endDate.value,
                endTime: endTime.value,
                addressLine: address.value,
                postalCode: postalCode.value,
                city: city.value
            }
        }

        const submit = () => {
            if (validateFields()) {

                showSpinner.value = true

                const fields = getFields()
               
                store.dispatch(submitPath, { 
                    body: fields, 
                    id: props.userInfo?.eventId,                     
                    pageId: store.state.main.page_id,
                    groupId: props.groupId, 
                })
                .catch(error => {
                    showSpinner.value = false
                })

            } else {
                displayError.value = true
            }
        }

        const updateContactType = (value) => {
            contactType.value = value;
        
            if (value === contactTypeEnum.SELF) {
                contactPersonMyself.value = true; 
                contactPersonOther.value = false; 
            } else if (value === contactTypeEnum.OTHER) {
                contactPersonMyself.value = false; 
                contactPersonOther.value = true;  
            }
        }

        return {
            nameOfEvent,
            description,
            shareInMainCalendar,
            mandatoryRegistration,
            maxParticipants,
            contactPersonMyself,
            contactPersonOther,
            contactPersonEmail,
            contactPersonName,
            contactPersonPhone,
            startDate,
            startTime,
            endDate,
            endTime,
            address,
            postalCode,
            city,
            showErrorAddress,
            isStartDateValid,
            isEndDateValid,
            isEditContext,
            showSpinner,
            contactType,
            contactTypeRadioOptions,
            showError,
            submit,
            getLabel,
            updateContactType,
            editorReady
        }
    }
}